import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  FormControlLabel, Switch, MenuItem,
} from '@mui/material';
import { Home, Add, Edit, Delete, AltRoute, WarningAmber, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const empty = { routeCode: '', name: '', stops: [], active: true };
const API = process.env.REACT_APP_API_URL;

export default function RouteSetup() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [delDlg, setDelDlg] = useState(null);
  const [newStop, setNewStop] = useState('');
  const { attemptClose, discardDialog } = useUnsavedGuard({
    open: dialogOpen, value: form, onClose: () => setDialogOpen(false),
    message: 'You have unsaved route details. Close without saving?',
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/api/fleet/routes`, { headers }),
        axios.get(`${API}/api/fleet/locations`, { headers }),
      ]);
      setRows(a.data); setLocations(b.data); setError('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to load routes'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const locName = (code) => locations.find(l => l.code === code)?.name || code;

  const openNew = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r.routeCode); setForm({ ...empty, ...r, stops: [...(r.stops || [])] }); setDialogOpen(true); };

  const addStop = () => {
    if (!newStop) return;
    if (form.stops.some(s => s.locationCode === newStop)) return;
    setForm({ ...form, stops: [...form.stops, { locationCode: newStop, sequence: form.stops.length + 1, approxTime: '' }] });
    setNewStop('');
  };
  const moveStop = (i, dir) => {
    const s = [...form.stops];
    const j = i + dir;
    if (j < 0 || j >= s.length) return;
    [s[i], s[j]] = [s[j], s[i]];
    s.forEach((x, idx) => x.sequence = idx + 1);
    setForm({ ...form, stops: s });
  };
  const removeStop = (i) => {
    const s = form.stops.filter((_, idx) => idx !== i);
    s.forEach((x, idx) => x.sequence = idx + 1);
    setForm({ ...form, stops: s });
  };
  const setStopTime = (i, t) => {
    const s = [...form.stops]; s[i] = { ...s[i], approxTime: t }; setForm({ ...form, stops: s });
  };

  const save = async () => {
    if (!editing && !form.routeCode.trim()) return setError('Route code is required');
    if (!form.name.trim()) return setError('Name is required');
    try {
      if (editing) await axios.put(`${API}/api/fleet/routes/${editing}`, form, { headers });
      else await axios.post(`${API}/api/fleet/routes`, form, { headers });
      setDialogOpen(false); setError(''); setSuccess(`Route ${editing ? 'updated' : 'created'}`); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/api/fleet/routes/${delDlg}`, { headers }); setDelDlg(null); setSuccess('Route deleted'); load(); }
    catch (err) { setDelDlg(null); setError(err.response?.data?.error || 'Failed to delete'); }
  };

  if (loading) return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}><CircularProgress /><Typography sx={{ mt: 2 }}>Loading routes...</Typography></Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center"><AltRoute sx={{ mr: 1, color: '#00838f' }} /><Typography variant="h5" sx={{ color: '#00838f' }}>Routes</Typography></Box>
            <Box>
              <IconButton onClick={() => navigate('/fleet')} title="Fleet Dashboard" sx={{ mr: 1 }}><Home /></IconButton>
              <IconButton onClick={openNew} title="Add Route"><Add /></IconButton>
            </Box>
          </Box>
          <Toast error={error} success={success} onClose={(k) => k === 'error' ? setError('') : setSuccess('')} />
          {rows.length === 0 ? <Typography color="text.secondary">No routes yet.</Typography> : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell><strong>Code</strong></TableCell><TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Stages (in order)</strong></TableCell><TableCell><strong>Status</strong></TableCell><TableCell><strong>Actions</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.routeCode} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.routeCode}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{(r.stops || []).sort((a, b) => a.sequence - b.sequence).map(s => locName(s.locationCode)).join(' → ') || '—'}</TableCell>
                    <TableCell><Chip label={r.active === false ? 'Inactive' : 'Active'} color={r.active === false ? 'default' : 'success'} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <IconButton color="primary" size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton>
                      <IconButton color="warning" size="small" onClick={() => setDelDlg(r.routeCode)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(delDlg)} onClose={() => setDelDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}><WarningAmber color="warning" /> Delete Route</DialogTitle>
        <DialogContent><Typography>Delete <strong>{delDlg}</strong>?</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDelDlg(null)}>Cancel</Button><Button variant="contained" color="warning" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? `Edit Route: ${editing}` : 'New Route'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Route Code" value={form.routeCode} disabled={!!editing} size="small" fullWidth
              onChange={e => setForm({ ...form, routeCode: e.target.value })} />
            <TextField label="Name" value={form.name} size="small" fullWidth onChange={e => setForm({ ...form, name: e.target.value })} />
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Stages along the route (order matters)</Typography>
            <Box display="flex" gap={1}>
              <TextField select label="Add stage" value={newStop} size="small" sx={{ flex: 1 }} onChange={e => setNewStop(e.target.value)}>
                {locations.filter(l => !form.stops.some(s => s.locationCode === l.code)).map(l => <MenuItem key={l.code} value={l.code}>{l.name}</MenuItem>)}
              </TextField>
              <Button variant="outlined" onClick={addStop}>Add</Button>
            </Box>
            {form.stops.length > 0 && (
              <Table size="small">
                <TableHead><TableRow><TableCell>#</TableCell><TableCell>Stage</TableCell><TableCell>Approx time</TableCell><TableCell></TableCell></TableRow></TableHead>
                <TableBody>
                  {form.stops.sort((a, b) => a.sequence - b.sequence).map((s, i) => (
                    <TableRow key={s.locationCode}>
                      <TableCell>{s.sequence}</TableCell>
                      <TableCell>{locName(s.locationCode)}</TableCell>
                      <TableCell><TextField size="small" placeholder="HH:mm" value={s.approxTime} onChange={e => setStopTime(i, e.target.value)} sx={{ width: 90 }} /></TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => moveStop(i, -1)}><ArrowUpward fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => moveStop(i, 1)}><ArrowDownward fontSize="small" /></IconButton>
                        <IconButton size="small" color="warning" onClick={() => removeStop(i)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {editing && <FormControlLabel control={<Switch checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />} label="Active" />}
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={attemptClose}>Cancel</Button><Button variant="contained" onClick={save}>Save</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
